import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { MARLEA } from "@/constants/testIds";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

const SELECTS = {
  aanvragen: ["Minder dan 10", "10 tot 25", "25 tot 50", "Meer dan 50"],
  klantwaarde: [
    "Minder dan €500",
    "€500 tot €1.500",
    "€1.500 tot €5.000",
    "Meer dan €5.000",
    "Weet ik niet",
  ],
  verbeterdoel: [
    "Meer aanvragen uit websitebezoekers",
    "Minder herhalende vragen",
    "Betere kwalificatie van leads",
    "Snellere opvolging",
    "Ik weet het nog niet",
  ],
  huidigebot: ["Nee", "Ja, maar basic", "Ja, professioneel"],
};

const empty = {
  naam: "",
  email: "",
  telefoon: "",
  bedrijfsnaam: "",
  website: "",
  sector: "",
  aanvragen_per_maand: "",
  klant_waarde: "",
  verbeterdoel: "",
  huidige_chatbot: "",
  toelichting: "",
  company_website_url: "", // honeypot
};

const inputCls =
  "bg-white/[0.03] border-white/10 text-white placeholder:text-[#6e7281] focus-visible:ring-[#00EFFF]/40 focus-visible:border-[#00EFFF]/40 h-11";

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-[0.14em] text-[#A7A7A7] font-mono-display">
      {label}
    </Label>
    {children}
  </div>
);

const SelectField = ({ testId, value, onChange, options, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      data-testid={testId}
      className="bg-white/[0.03] border-white/10 text-white h-11 focus:ring-[#00EFFF]/40"
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent className="bg-[#0A0A1A] border-white/10 text-white">
      {options.map((o) => (
        <SelectItem
          key={o}
          value={o}
          data-testid={`${testId}-option-${o.replace(/\s+/g, "-").toLowerCase()}`}
          className="focus:bg-[#00EFFF]/10 focus:text-white"
        >
          {o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const DemoForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["naam", "email", "bedrijfsnaam", "website", "sector"];
    for (const k of required) {
      if (!form[k] || !String(form[k]).trim()) {
        toast.error("Vul alle verplichte velden in.");
        return;
      }
    }
    setLoading(true);
    try {
      await submitLead(form);
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Er ging iets mis. Probeer het opnieuw.";
      toast.error(typeof msg === "string" ? msg : "Er ging iets mis.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-16 md:py-24 scroll-mt-24">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute top-10 left-1/2 -translate-x-1/2 h-[420px] w-[820px] blur-3xl opacity-25"
          style={{
            background:
              "radial-gradient(ellipse, rgba(0,239,255,0.35), rgba(168,255,0,0.15) 40%, transparent 70%)",
          }}
        />
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center">
          <span className="text-[11px] uppercase tracking-[0.22em] font-mono-display text-[#00FFB7]">
            Demo aanvragen
          </span>
          <h2 className="mt-4 font-display font-semibold text-white text-3xl md:text-[42px] tracking-tight leading-[1.1]">
            Vraag uw gratis <span className="text-gradient-neon">demo aan</span>
          </h2>
          <p className="mt-5 text-[#A7A7A7] text-base md:text-[17px] leading-relaxed">
            Laat uw gegevens achter. We bekijken uw type bedrijf en tonen hoe een 24/7
            AI-chatbot bezoekers kan helpen, vragen kan beantwoorden en aanvragen kan
            verzamelen.
          </p>
        </div>

        <div className="mt-10 glass-strong rounded-3xl p-6 md:p-8 relative overflow-hidden">
          <div
            className="absolute -top-32 -right-20 h-64 w-64 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(168,255,0,0.25), transparent 70%)",
            }}
          />

          {!done ? (
            <form onSubmit={handleSubmit} className="relative">
              {/* Honeypot — visually hidden, real users won't fill it */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label>
                  Company website URL (laat leeg)
                  <input
                    type="text"
                    name="company_website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company_website_url}
                    onChange={(e) => setField("company_website_url", e.target.value)}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Naam *">
                  <Input
                    data-testid={MARLEA.intakeInputNaam}
                    value={form.naam}
                    onChange={(e) => setField("naam", e.target.value)}
                    placeholder="Uw naam"
                    className={inputCls}
                  />
                </Field>
                <Field label="E-mailadres *">
                  <Input
                    type="email"
                    data-testid={MARLEA.intakeInputEmail}
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="naam@bedrijf.be"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefoonnummer">
                  <Input
                    data-testid={MARLEA.intakeInputTelefoon}
                    value={form.telefoon}
                    onChange={(e) => setField("telefoon", e.target.value)}
                    placeholder="+32 ..."
                    className={inputCls}
                  />
                </Field>
                <Field label="Bedrijfsnaam *">
                  <Input
                    data-testid={MARLEA.intakeInputBedrijf}
                    value={form.bedrijfsnaam}
                    onChange={(e) => setField("bedrijfsnaam", e.target.value)}
                    placeholder="Bedrijfsnaam"
                    className={inputCls}
                  />
                </Field>
                <Field label="Website *">
                  <Input
                    data-testid={MARLEA.intakeInputWebsite}
                    value={form.website}
                    onChange={(e) => setField("website", e.target.value)}
                    placeholder="www.uwbedrijf.be"
                    className={inputCls}
                  />
                </Field>
                <Field label="Sector / type bedrijf *">
                  <Input
                    data-testid={MARLEA.intakeInputSector}
                    value={form.sector}
                    onChange={(e) => setField("sector", e.target.value)}
                    placeholder="Bijv. eventlocatie, kliniek, B2B service..."
                    className={inputCls}
                  />
                </Field>

                <Field label="Aanvragen per maand">
                  <SelectField
                    testId={MARLEA.intakeSelectAanvragen}
                    value={form.aanvragen_per_maand}
                    onChange={(v) => setField("aanvragen_per_maand", v)}
                    options={SELECTS.aanvragen}
                    placeholder="Kies een bereik"
                  />
                </Field>
                <Field label="Wat is een nieuwe klant gemiddeld waard?">
                  <SelectField
                    testId={MARLEA.intakeSelectKlantwaarde}
                    value={form.klant_waarde}
                    onChange={(v) => setField("klant_waarde", v)}
                    options={SELECTS.klantwaarde}
                    placeholder="Kies een bereik"
                  />
                </Field>
                <Field label="Wat wilt u vooral verbeteren?" className="md:col-span-2">
                  <SelectField
                    testId={MARLEA.intakeSelectVerbeterdoel}
                    value={form.verbeterdoel}
                    onChange={(v) => setField("verbeterdoel", v)}
                    options={SELECTS.verbeterdoel}
                    placeholder="Kies een doel"
                  />
                </Field>
                <Field label="Heeft u al een chatbot of livechat?" className="md:col-span-2">
                  <SelectField
                    testId={MARLEA.intakeSelectHuidigeBot}
                    value={form.huidige_chatbot}
                    onChange={(v) => setField("huidige_chatbot", v)}
                    options={SELECTS.huidigebot}
                    placeholder="Kies een optie"
                  />
                </Field>
                <Field label="Korte toelichting / grootste uitdaging" className="md:col-span-2">
                  <Textarea
                    data-testid={MARLEA.intakeInputToelichting}
                    value={form.toelichting}
                    onChange={(e) => setField("toelichting", e.target.value)}
                    placeholder="Optioneel. Vertel kort waar u tegenaan loopt."
                    rows={3}
                    className={inputCls + " resize-none"}
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid={MARLEA.intakeSubmit}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-neon px-7 py-4 text-[15px] font-semibold text-[#0A0A1A] transition-transform duration-200 hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed glow-cyan"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Bezig met verzenden...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Vraag gratis demo aan
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-[#A7A7A7]">
                Geen verplichting. Uw gegevens worden enkel gebruikt om uw demo voor te
                bereiden.
              </p>
            </form>
          ) : (
            <div
              data-testid={MARLEA.intakeThanks}
              className="relative px-2 py-10 md:py-12 text-center"
            >
              <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-neon glow-cyan">
                <CheckCircle2 className="h-8 w-8 text-[#0A0A1A]" strokeWidth={2.5} />
              </div>
              <h3 className="mt-6 font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                Bedankt! <span className="text-gradient-neon">Aanvraag ontvangen.</span>
              </h3>
              <p className="mt-4 text-[#A7A7A7] text-base md:text-[17px] leading-relaxed max-w-md mx-auto">
                We hebben uw aanvraag ontvangen. We nemen binnenkort contact met u op
                met meer info over de demo.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DemoForm;
